import type { SelectProps, SelectTriggerProps } from "@radix-ui/react-select";
import type { FieldValues, Path, UseFormReturn } from "react-hook-form";

import {
  BaseSelect,
  CustomLabel,
  FieldErrorMessage,
  type ItemsListType,
} from "@/components/atoms";
import { FormControl, FormField, FormItem } from "@/components/ui/form";
import { cn } from "@/lib/utils";

type SelectPropsWithoutForm = Omit<SelectProps, "form">;

interface CustomSelectProps<T extends FieldValues>
  extends Partial<SelectPropsWithoutForm> {
  itemsList: ItemsListType;
  form: UseFormReturn<T>;
  nameSelect: Path<T>;
  label?: string | React.ReactNode;
  classes?: string;
  placeholder?: string;
  isTree?: boolean;
  triggerProps?: Partial<SelectTriggerProps> & { "data-testid"?: string };
  isSearchable?: boolean;
}

const SelectControl = <T extends FieldValues>({
  itemsList,
  form,
  nameSelect,
  label,
  placeholder = "",
  disabled,
  classes = "w-full",
  isTree = false,
  triggerProps,
  isSearchable,
  onValueChange,
}: CustomSelectProps<T>) => {
  return (
    <FormField
      control={form.control}
      name={nameSelect}
      render={({ field, fieldState }) => (
        <FormItem
          className={cn("relative", classes)}
          data-testid={`select-form-item-${nameSelect}`}
        >
          {label && <CustomLabel htmlFor={nameSelect} label={label} />}
          <FormControl>
            <BaseSelect
              value={field.value ?? ""}
              onChange={value => {
                field.onChange(value);
                onValueChange?.(value);
              }}
              itemsList={itemsList}
              placeholder={placeholder}
              disabled={disabled}
              triggerProps={triggerProps}
              isTree={isTree}
              hasError={!!fieldState.error}
              isSearchable={isSearchable}
            />
          </FormControl>
          <FieldErrorMessage />
        </FormItem>
      )}
    />
  );
};

export default SelectControl;
